import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { usePermissions } from '@/contexts/PermissionsContext';
import { useNavigation } from '@/contexts/NavigationContext';
import { navItemForPath } from '@/lib/navigation-catalog';
import { permissionsForPath } from './navigation-items';
import { NothingAvailable } from './NavigationUnavailable';

/**
 * Typing the address of a screen the role may not open takes the person Home instead of showing the screen. Two things must hold, in this order:
 *   1. the NAVIGATION matrix (contexts/NavigationContext.tsx): the element the address belongs to (lib/navigation-catalog.ts) -- and every ancestor -- is ON for the
 *      role in the active company. An address that belongs to no element is not reachable at all. While the matrix is not loaded nothing is rendered.
 *   2. the PERMISSION matrix: the role holds the View permission of the resource (when permissions cannot be read, nothing is blocked -- see PermissionsContext).
 * Not the boundary -- the server refuses the same requests (the API of a module that is OFF, and the permission / RLS layers) -- but the screen no longer even tries
 * to show, nor loads the data of, what is not theirs.
 */
export function RoutePermissionGuard({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const { ready, canAny } = usePermissions();
  const { status, isVisibleKey } = useNavigation();
  if (status !== 'loaded') return null; // the shell shows the loading / error state; a protected page is never rendered meanwhile

  const item = navItemForPath(pathname);
  if (!item || !isVisibleKey(item.key)) {
    // Home itself is closed: there is nowhere to redirect to
    if (pathname === '/') return <NothingAvailable message="Nothing is available for your account yet." />;
    return <Navigate to="/" replace />;
  }
  const required = permissionsForPath(pathname);
  if (ready && required && !canAny(required)) return pathname === '/' ? <>{children}</> : <Navigate to="/" replace />;
  return <>{children}</>;
}
